/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { OverlayProps, useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { AriaDialogProps } from '@react-types/dialog';
import * as React from 'react';
import { useFullViewport } from '@front10/utils';

interface ModalDialogProps extends React.PropsWithChildren<OverlayProps & AriaDialogProps> {
  title?: React.ReactElement;
  size?: 'default' | 'fullscreen' | 'stretch';
  autoFocus?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  isDismissable?: boolean;
}

export function ModalDialog(props: ModalDialogProps) {
  let { title, children, size = 'default', autoFocus = false } = props;

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  let ref = React.useRef(null);
  let { overlayProps, underlayProps } = useOverlay(props, ref);

  // Prevent scrolling while the modal is open, and hide content
  // outside the modal from screen readers.
  usePreventScroll();
  let { modalProps } = useModal();

  useFullViewport(props.isOpen || false);

  // Get props for the dialog and its title
  let { dialogProps, titleProps } = useDialog(props, ref);

  return (
    <div
      sx={{
        variant: 'layout.dialog',
        position: 'fixed',
        zIndex: size === 'fullscreen' ? 160 : 100,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        bg: 'var(--color-overlay, rgba(0, 0, 0, 0.8))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      {...underlayProps}
    >
      <FocusScope contain restoreFocus autoFocus={autoFocus}>
        <div
          {...overlayProps}
          {...dialogProps}
          {...modalProps}
          ref={ref}
          sx={{
            bg: 'backgroundMain',
            position: 'relative',
            isolation: 'isolate',
            height: size === 'fullscreen' || size === 'stretch' ? '100%' : undefined,
            width: size === 'stretch' ? '100%' : undefined
          }}
        >
          {title ? React.cloneElement(title, titleProps) : null}
          {children}
        </div>
      </FocusScope>
    </div>
  );
}
